import type { PasswordComplexityRule } from "@auth0/auth0-acul-react/types";

/**
 * The hosted "Your password must contain:" box, measured live 2026-10-13:
 *
 *   8px below the second field, 1px theme input border, 3px radius,
 *   14px x 16px padding, 14px / 20px text in the body-text colour.
 *   Rows 8px apart. Each rule has a marker 24px to its left: grey "•" when
 *   unmet, "✓" in the theme SUCCESS colour when met (text turns success too).
 *   Nested rules ("At least 3 of the following:") indent another 24px.
 *
 * Rules and their labels are NOT hard-coded. They come from the screen's
 * transaction.passwordPolicy via the SDK's usePasswordValidation, which reads
 * Auth0's own `passwordSecurityInfo`. So a policy change in
 * auth0_connection.database reaches this box with no ACUL rebuild, exactly as
 * it reaches the hosted box.
 *
 * Separate from ULThemePasswordValidator (signup) because that one draws
 * nested rules without markers and on a different box. Changing it would
 * change the signup screen.
 */
interface PasswordPolicyChecklistProps {
  title: string;
  rules: PasswordComplexityRule[];
}

function Rule({ rule }: { rule: PasswordComplexityRule }) {
  const color = rule.isValid
    ? "var(--ul-theme-color-success, #00a651)"
    : "var(--ul-theme-color-body-text, #4b5563)";
  return (
    <li className="relative mt-2" data-valid={rule.isValid}>
      <span
        aria-hidden="true"
        className="absolute -left-6 w-[10px] text-center"
        style={{ color }}
      >
        {rule.isValid ? "✓" : "•"}
      </span>
      <span style={{ color }}>{rule.label}</span>
      {rule.items && rule.items.length > 0 ? (
        <ul className="m-0 list-none pl-6">
          {rule.items.map((item) => (
            <Rule key={item.code} rule={item} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export default function PasswordPolicyChecklist({
  title,
  rules,
}: PasswordPolicyChecklistProps) {
  if (!rules.length) return null;
  return (
    <div
      className="mt-2 rounded-[3px] px-4 py-[14px]"
      style={{
        border:
          "var(--ul-theme-border-input-border-weight, 1px) solid var(--ul-theme-color-input-border, #d1d5db)",
        fontSize: "var(--ul-theme-font-body-text-size, 0.875rem)",
        lineHeight: "20px",
        color: "var(--ul-theme-color-body-text, #4b5563)",
      }}
      aria-live="polite"
    >
      <div>{title}</div>
      <ul className="m-0 list-none pl-6">
        {rules.map((rule) => (
          <Rule key={rule.code} rule={rule} />
        ))}
      </ul>
    </div>
  );
}
